import React, { useEffect, useState } from 'react';
import '../../styles/EventSummaryCard.css';
import { logger } from '../../utils/logger';
import { checkUserEventApplication } from '../../services/applicationService';
import { User } from 'firebase/auth';
// Import font awesome icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faClock, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';

interface EventSummaryCardProps {
  title: string;
  description: string;
  imageSrc: string;
  theme: string;
  linkHref: string;
  linkText?: string;
  date?: string;
  time?: string;
  location?: string;
  eventId: string; // Required event UID from Firestore
  showDescription?: boolean;
  currentUser?: User | null;
}

const EventSummaryCard: React.FC<EventSummaryCardProps> = ({
  title,
  description,
  imageSrc,
  theme,
  linkHref,
  linkText = 'Learn more',
  date,
  time,
  location,
  eventId,
  showDescription = true,
  currentUser
}) => {
  const [hasApplied, setHasApplied] = useState<boolean>(false);
  const [applicationId, setApplicationId] = useState<string | undefined>(undefined);
  const [checkingApplication, setCheckingApplication] = useState<boolean>(false);

  useEffect(() => {
    logger.info('🎴 EventSummaryCard rendered', { component: 'EventSummaryCard', title });
    return () => {
      logger.info('🎴 EventSummaryCard unmounted', { component: 'EventSummaryCard', title });
    };
  }, [title]);

  // Check if the current user has applied to this event
  useEffect(() => {
    const checkApplicationStatus = async () => {
      if (!currentUser) {
        setHasApplied(false);
        return;
      }

      setCheckingApplication(true);
      try {
        logger.info('🔍 Checking application status for event card', { 
          component: 'EventSummaryCard', 
          eventId,
          userId: currentUser.uid 
        });
        
        const result = await checkUserEventApplication(eventId);
        setHasApplied(result.hasApplied);
        setApplicationId(result.applicationId);
        
        if (result.hasApplied) {
          logger.info('✅ User has applied to this event', { 
            component: 'EventSummaryCard',
            eventId,
            applicationId: result.applicationId 
          });
        }
      } catch (error) {
        logger.error('❌ Error checking application status for event card:', error);
        setHasApplied(false);
      } finally {
        setCheckingApplication(false);
      }
    };

    checkApplicationStatus();
  }, [currentUser, eventId]);

  // Truncate description if it's too long
  const truncateDescription = (text: string, maxLength = 100): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Get the display text for the link based on application status
  const getDisplayLinkText = (): string => {
    if (checkingApplication) {
      return 'Checking status...';
    }
    
    if (hasApplied) {
      return 'Check your application status';
    }
    
    return linkText || 'Learn more';
  };

  // Use the provided linkHref for the event URL or application status URL
  const eventUrl = (() => {
    try {
      // If user has applied, link to application status page
      if (hasApplied && applicationId) {
        return `/eventapplication/${applicationId}`;
      }
      
      // Otherwise, use the provided linkHref (usually event detail page)
      const url = new URL(linkHref, window.location.origin);
      url.searchParams.set('eventId', eventId);
      return url.toString();
    } catch (error) {
      logger.error('Invalid URL provided for linkHref', { linkHref, error });
      return linkHref; // Fallback to the original linkHref if parsing fails
    }
  })();

  const handleCardClick = () => {
    if (hasApplied && applicationId) {
      logger.info('🖱️ Event card clicked - navigating to application status', { 
        component: 'EventSummaryCard', 
        title, 
        eventId, 
        applicationId 
      });
    } else {
      logger.info('🖱️ Event card clicked - navigating to event details', { 
        component: 'EventSummaryCard', 
        title, 
        eventId 
      });
    }
    window.location.href = eventUrl;
  };

  return (
    <div 
      className="event-card" 
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      aria-label={`${title} event on ${date}. Click to apply`}
      onKeyDown={(e) => {if (e.key === 'Enter' || e.key === ' ') handleCardClick()}}
    >
      <div 
        className="event-image" 
        style={{ backgroundImage: `url(${imageSrc})` }}
      >
        <div className="event-theme">{theme}</div>
      </div>
      <div className="event-details">
        <h3>{title}</h3>
        
        {(date || time || location) && (
          <div className="event-metadata">
            {date && (
              <div className="event-date">
                <span className="metadata-icon">
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </span>
                {date}
              </div>
            )}
            {time && (
              <div className="event-time">
                <span className="metadata-icon">
                  <FontAwesomeIcon icon={faClock} />
                </span>
                {time}
              </div>
            )}
            {location && (
              <div className="event-location">
                <span className="metadata-icon">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                </span>
                {location}
              </div>
            )}
          </div>
        )}
        
        {showDescription && <p>{truncateDescription(description)}</p>}
        <div className="event-link">
          {getDisplayLinkText()} <span className="arrow">→</span>
        </div>
      </div>
    </div>
  );
};

export default EventSummaryCard;
